/**
 * Brand Setup — the public product's front door, as a STEPPER (spec
 * `2026-09-16-brand-setup-stepper-and-focus-editor-design.md` §2).
 *
 * One decision per screen, a live phone beside it, a chapter rail that says
 * where you are, and a Continue that is never disabled — clicking it with
 * something missing focuses the control and says what is missing, instead of
 * greying out with no reason (the defect the operator hit on his friend's
 * brand, §0).
 *
 * This file is the SHELL: layout, navigation, the study poller, the asset
 * handlers, the wordmark, the draft. It owns no brand logic —
 *   - `lib/brandSetup/steps.ts`  decides which screens this lane walks,
 *   - `lib/brandSetup/state.ts`  holds every value and the study folding,
 *   - `lib/brandSetup/payload.ts` turns it into the SAME `CreateChannelInput`
 *     the classic wizard posts (pinned by `payload.test.ts`), and
 *   - `lib/brandSetup/draft.ts`  decides what survives a refresh.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Sparkles } from "lucide-react";
import {
  CreateChannelError,
  activateChannelApi,
  createChannelApi,
  getSettings,
  listChannelsApi,
  listFontFamiliesApi,
  type FontFamilyEntry,
} from "../api";
import {
  LOGO_CHIP_PREFIX,
  brandSetupReducer,
  effectiveDesign,
  initialBrandSetupState,
  studyFoundLogo,
} from "../lib/brandSetup/state";
import {
  CHAPTERS,
  STEP_COPY,
  nextStep,
  prevStep,
  stepNumber,
  studiedStepsWithChips,
  visibleSteps,
  type StepId,
} from "../lib/brandSetup/steps";
import { clearDraft } from "../lib/brandSetup/draft";
import { buildBrandSetupPayload, effectiveSlug, slugify } from "../lib/brandSetup/payload";
import { useMinWidth } from "../lib/useIsMobile";
import { OnboardingStudio, clearOnboardingDesignDraft } from "./OnboardingStudio";
import { BRAND_SETUP_DRAFT_KEY } from "./brandSetup/keys";
import { Confirm } from "./brandSetup/Confirm";
import { ProgressBar } from "./brandSetup/ProgressBar";
import { PHONE_SHOT, SamplePhone } from "./brandSetup/SamplePhone";
import { StepFrame, StepHeader, type StepFrameProps } from "./brandSetup/StepFrame";
import { TimelineRail } from "./brandSetup/TimelineRail";
import { WizardLayout } from "./brandSetup/WizardLayout";
import { useAdjustLift } from "./brandSetup/hooks/useAdjustLift";
import { useBrandAssets } from "./brandSetup/hooks/useBrandAssets";
import {
  QUOTA_SENTENCE,
  READ_INTERRUPTED_SENTENCE,
  readDraftOnce,
  useBrandDraft,
} from "./brandSetup/hooks/useBrandDraft";
import { useBrandStudy } from "./brandSetup/hooks/useBrandStudy";
import { useSampleProps } from "./brandSetup/hooks/useSampleProps";
import { BasicsStep } from "./brandSetup/steps/BasicsStep";
import { CaptionPositionStep } from "./brandSetup/steps/CaptionPositionStep";
import { CaptionSizeStep } from "./brandSetup/steps/CaptionSizeStep";
import { ColorsStep } from "./brandSetup/steps/ColorsStep";
import { DoneStep, type FinishIntent } from "./brandSetup/steps/DoneStep";
import { FontStep } from "./brandSetup/steps/FontStep";
import { LogoStep } from "./brandSetup/steps/LogoStep";
import { PathStep } from "./brandSetup/steps/PathStep";
import { ReadingStep } from "./brandSetup/steps/ReadingStep";
import { ReviewStep } from "./brandSetup/steps/ReviewStep";
import { SignoffStep } from "./brandSetup/steps/SignoffStep";
import { SourcesStep } from "./brandSetup/steps/SourcesStep";
import { SpeakerStep } from "./brandSetup/steps/SpeakerStep";
import { WelcomeStep } from "./brandSetup/steps/WelcomeStep";

/** Every step in canonical order — the automatic lane IS the superset. Used to
 *  place a step that the CURRENT lane hides (see `furthestVisible`). */
const CANONICAL_ORDER: readonly StepId[] = visibleSteps({ mode: "auto" }).map((d) => d.id);


export interface BrandSetupProps {
  /**
   * The channel exists and is active by the time this fires.
   *
   * `intent` is where the operator asked to land — a sample video, their own
   * clip (the upload lane) or the library. `tryVideo` is the same answer in
   * the shape the app shell reads today; Task 10 takes the intent itself.
   */
  onDone: (result: { slug: string; intent: FinishIntent; tryVideo: boolean }) => void;
  onCancel: () => void;
}

/**
 * "Start over" is a REMOUNT, not a reducer action: the draft is cleared and
 * the flow is built again from `initialBrandSetupState()`, so there is no
 * chance of a stale chip, a claimed field or a half-reset value surviving the
 * gesture. (A `patch` of the initial state would claim `brandName` on the way
 * through — the fresh state must be genuinely fresh.)
 */
export function BrandSetup(props: BrandSetupProps): React.JSX.Element {
  const [generation, setGeneration] = useState(0);
  return (
    <BrandSetupFlow
      key={generation}
      {...props}
      onStartOver={() => {
        clearDraft(localStorage);
        clearOnboardingDesignDraft(BRAND_SETUP_DRAFT_KEY);
        setGeneration((g) => g + 1);
      }}
    />
  );
}

function BrandSetupFlow({
  onDone,
  onCancel,
  onStartOver,
}: BrandSetupProps & { onStartOver: () => void }): React.JSX.Element {
  const wide = useMinWidth(900);
  const roomy = useMinWidth(1100);

  // ---- state -------------------------------------------------------------

  const [restored] = useState(readDraftOnce);
  const [state, dispatch] = useReducer(brandSetupReducer, restored, (r) => {
    if (!r) return initialBrandSetupState();
    // A draft ALWAYS opens on welcome (spec §2.2, welcome › cases): the screen
    // says "Pick up where you left off" — with the rail already ticked and the
    // phone already seeded — and Start jumps to the saved step. Dropping
    // someone straight back into step 7 with no explanation is the thing that
    // screen exists to prevent. `furthest` is untouched, so nothing is lost.
    // (`reading` can never be the resume target either: it is uncounted, and
    // `hasDraft` reports the next COUNTED step — a reading screen with no
    // poller attached to it is exactly what that rule avoids.)
    return { ...r.state, step: "welcome" as StepId, returnTo: null };
  });
  const [furthest, setFurthest] = useState<StepId>(restored?.furthest ?? "welcome");
  const [libraryFamilies, setLibraryFamilies] = useState<FontFamilyEntry[]>([]);
  const [brands, setBrands] = useState<Array<{ slug: string; displayName: string }>>([]);
  const [archivedBrands, setArchivedBrands] = useState<Array<{ slug: string; displayName: string }>>([]);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  // QL_SAMPLES. An older server (or a blip) reads as ON, the same convention
  // NewJob uses — an empty catalog already renders honestly.
  const [samplesOn, setSamplesOn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set once the POST landed — what stops a Done CTA creating a second one. */
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  /** A create failure that belongs to the NAME field, shown there on arrival. */
  const [createReason, setCreateReason] = useState<string | null>(null);
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [confirmStopReading, setConfirmStopReading] = useState(false);
  const [vh, setVh] = useState(() => (typeof window === "undefined" ? 900 : window.innerHeight));

  const rootRef = useRef<HTMLDivElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const linkRef = useRef<HTMLInputElement | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);

  // ---- ambient -----------------------------------------------------------

  // How much of the viewport is ABOVE us. Zero once Brand Setup renders in
  // focus mode (Task 10); today the app shell's mobile header sits there, and
  // a plain `100dvh` column would hang off the bottom of the screen — which is
  // what pushed the phone's own top bar out of sight.
  const [topInset, setTopInset] = useState(0);
  useEffect(() => {
    const measure = (): void => {
      setVh(window.innerHeight);
      const rect = rootRef.current?.getBoundingClientRect();
      setTopInset(rect ? Math.max(0, Math.round(rect.top + window.scrollY)) : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    void getSettings()
      .then((s) => setSamplesOn(s.features?.samples !== false))
      .catch(() => { /* older server / blip — keep the default */ });
  }, []);

  useEffect(() => {
    void listFontFamiliesApi()
      .then(setLibraryFamilies)
      .catch(() => setLibraryError("We couldn't load the font library just now — the default faces still work."));
  }, []);

  // Fetched ONCE: the brand list only tells us which names are taken, and a
  // request per keystroke would be a lot of noise for one sentence. Archived
  // brands are fetched too — the server's slug check doesn't care whether a
  // match is active or archived, so the client check must not either.
  useEffect(() => {
    void Promise.all([listChannelsApi(), listChannelsApi({ archived: true })])
      .then(([live, arch]) => {
        setBrands(live.channels.map((c) => ({ slug: c.slug, displayName: c.displayName })));
        setArchivedBrands(arch.channels.map((c) => ({ slug: c.slug, displayName: c.displayName })));
      })
      .catch(() => { /* no list, no conflict check — Create still reports it */ });
  }, []);

  // A file dropped anywhere OUTSIDE a drop zone would otherwise be opened by
  // the browser, navigating away from a half-finished wizard.
  useEffect(() => {
    const swallow = (e: DragEvent): void => e.preventDefault();
    window.addEventListener("dragover", swallow);
    window.addEventListener("drop", swallow);
    return () => {
      window.removeEventListener("dragover", swallow);
      window.removeEventListener("drop", swallow);
    };
  }, []);

  // ---- navigation --------------------------------------------------------

  const visible = useMemo(() => visibleSteps(state), [state]);
  const indexOfStep = useCallback(
    (id: StepId): number => visible.findIndex((d) => d.id === id),
    [visible],
  );

  /**
   * `furthest`, as THIS lane can show it.
   *
   * Changing lane can hide the step it points at — walk auto → Sources, go
   * Back, pick "Start from our defaults", and `furthest` is `"sources"`, a
   * step the manual rail does not list. Every "is this reachable?" comparison
   * then answered `-1`, so the rail unlocked nothing at all. Pull it back to
   * the last step this lane shows at or before where it was.
   */
  const furthestVisible = useMemo<StepId>(() => {
    if (visible.some((d) => d.id === furthest)) return furthest;
    const canonical = CANONICAL_ORDER.indexOf(furthest);
    const before = visible.filter((d) => CANONICAL_ORDER.indexOf(d.id) <= canonical);
    return (before[before.length - 1] ?? visible[0]!).id;
  }, [visible, furthest]);

  const go = useCallback(
    (id: StepId, returnTo?: StepId | null): void => {
      if (id !== "basics") setCreateReason(null);
      dispatch({ type: "step", step: id, ...(returnTo !== undefined ? { returnTo } : {}) });
      setFurthest((f) => (visible.findIndex((d) => d.id === id) > visible.findIndex((d) => d.id === f) ? id : f));
    },
    [visible],
  );

  // ---- study, draft, Adjust — the three stateful subsystems ---------------

  const study = useBrandStudy(state.sources, dispatch);
  const draft = useBrandDraft({ state, furthest: furthestVisible, restored });
  const adjust = useAdjustLift(state.step, dispatch);
  const resume = draft.resume;

  // ---- assets, the wordmark, the live sample ------------------------------

  const reachedLogo = indexOfStep(furthestVisible) >= indexOfStep("logo") || state.step === "logo";
  const passedLogo = indexOfStep(furthestVisible) > indexOfStep("logo");
  // The study saw a logo on the reel: the placeholder holds its spot through
  // the logo step and the wordmark waits until the operator continues past it.
  const foundLogo = studyFoundLogo(state);
  const assets = useBrandAssets({
    state,
    dispatch,
    reachedLogo,
    passedLogo,
    studyFoundLogo: foundLogo,
    needsLogo: restored?.needsLogo ?? false,
    needsFont: restored?.needsFont ?? false,
  });
  const sample = useSampleProps(state, libraryFamilies);
  const brandName = state.brandName.trim();

  // ---- slug availability -------------------------------------------------

  /**
   * The brand this name would collide with, decided SYNCHRONOUSLY against the
   * list fetched once at mount. It used to be debounced state, which meant a
   * Continue pressed straight after typing read the previous name's verdict —
   * and the same call is what clears the sentence the moment the name changes.
   */
  const takenBy = useCallback((): { displayName: string; archived: boolean } | null => {
    const slug = effectiveSlug(state);
    if (!slug) return null;
    const live = brands.find((b) => b.slug === slug);
    if (live) return { displayName: live.displayName, archived: false };
    const archived = archivedBrands.find((b) => b.slug === slug);
    if (archived) return { displayName: archived.displayName, archived: true };
    return null;
  }, [state, brands, archivedBrands]);

  // ---- create ------------------------------------------------------------

  const slug = effectiveSlug(state);

  /**
   * Create the brand, then either land on `done` (`after === null`) or leave
   * for the app with the operator's intent.
   *
   * The POST happens on REVIEW, not on Done: Done says "{Brand} is set", and
   * a screen that reports a finished thing must not be the screen that does
   * it. The `after` argument is what keeps the Done buttons honest anyway —
   * if the create failed and the operator somehow reached Done, its CTA
   * creates and then leaves, instead of handing the app a slug that isn't
   * there.
   */
  const createBrand = useCallback(
    async (after: FinishIntent | null): Promise<void> => {
      setSubmitting(true);
      setError(null);
      const payload = buildBrandSetupPayload(state);
      try {
        const created = await createChannelApi(payload);
        await activateChannelApi(created.slug);
        draft.stopSaving();
        clearDraft(localStorage);
        clearOnboardingDesignDraft(BRAND_SETUP_DRAFT_KEY);
        setCreatedSlug(created.slug);
        setSubmitting(false);
        if (after === null) go("done");
        else onDone({ slug: created.slug, intent: after, tryVideo: after === "sample" });
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        const status = e instanceof CreateChannelError ? e.status : 0;
        setError(message);
        setSubmitting(false);
        // The one failure this screen cannot fix: the name is taken. The brand
        // list was fetched once at mount, so it does not know about a channel
        // created since — teaching it here is what makes the step's own check
        // agree with the server instead of waving the operator through to a
        // second identical failure. Then send them to the field that owns it,
        // sentence and all; Continue and Back both come back here (spec §8).
        if (isNameTaken(status, message)) {
          setBrands((prev) =>
            prev.some((b) => b.slug === payload.slug)
              ? prev
              : [...prev, { slug: payload.slug, displayName: payload.displayName }],
          );
          setCreateReason(message);
          go("basics", "review");
        }
      }
    },
    [go, onDone, state],
  );

  /** A Done CTA: leave if the brand is already there, create-then-leave if a
   *  Create on Review had failed. */
  const leaveWith = useCallback(
    (intent: FinishIntent): void => {
      if (createdSlug) onDone({ slug: createdSlug, intent, tryVideo: intent === "sample" });
      else void createBrand(intent);
    },
    [createBrand, createdSlug, onDone],
  );

  // ---- the current screen ------------------------------------------------

  const def = visible.find((d) => d.id === state.step) ?? visible[0]!;
  const chapterLabel = CHAPTERS.find((c) => c.id === def.chapter)?.label ?? "";
  const counter = stepNumber(state, state.step);
  const copy = STEP_COPY[state.step];
  const copyCtx = { logoKind: state.logoKind, studyFoundLogo: foundLogo };
  const headline = copy.headline(brandName, copyCtx);
  const why = copy.whyFor?.(copyCtx) ?? copy.why;
  const studied = useMemo(() => new Set(studiedStepsWithChips(state.chips)), [state.chips]);
  const settled = !study.studying;
  const matched = studied.size;

  const onContinueDefault = (): void =>
    go(nextStep(state, state.step, { keepAll: state.keptAll, returnTo: state.returnTo }));
  const onBackDefault = (): void => go(prevStep(state, state.step, { returnTo: state.returnTo }));

  /**
   * The height Adjust's stage may occupy: the viewport, minus whatever sits
   * above the wizard, minus the column's own padding and the "Done adjusting"
   * row under it. The Focus editor sizes the preview to fit THIS, which is
   * what keeps the whole editor inside one screen with no page scroll (§3).
   */
  const stageHeight = Math.max(360, vh - topInset - 150);

  const framed = (body: ReactNode, opts?: Partial<StepFrameProps>): React.JSX.Element => (
    <StepFrame
      key={state.step}
      chapter={chapterLabel}
      n={counter?.n ?? null}
      total={counter?.total ?? null}
      headline={headline}
      why={why}
      validate={() => null}
      onContinue={onContinueDefault}
      onBack={onBackDefault}
      {...opts}
    >
      {body}
    </StepFrame>
  );

  const stepView = ((): ReactNode => {
    switch (state.step) {
      case "welcome":
        return (
          <WelcomeStep
            brandName={brandName}
            resume={resume}
            note={draft.readInterrupted ? READ_INTERRUPTED_SENTENCE : null}
            onStart={() => go(resume ? resume.step : nextStep(state, "welcome"))}
            onStartOver={onStartOver}
          />
        );

      case "basics":
        return framed(<BasicsStep state={state} dispatch={dispatch} nameRef={nameRef} />, {
          initialReason: createReason,
          validate: () => {
            if (!state.brandName.trim()) return { reason: "Give your brand a name", focus: nameRef.current };
            const taken = takenBy();
            if (taken)
              return {
                reason: taken.archived
                  ? "You archived a brand with this name. Restore it from Settings › Brand, or pick another name."
                  : `You already have a brand called ${taken.displayName} — pick another name`,
                focus: nameRef.current,
              };
            return null;
          },
        });

      case "path":
        return framed(<PathStep state={state} dispatch={dispatch} cardsRef={cardsRef} />, {
          validate: () =>
            state.mode === null
              ? { reason: "Pick one to continue", focus: cardsRef.current?.querySelector("button") ?? null }
              : null,
        });

      case "sources":
        return framed(
          <SourcesStep
            state={state}
            dispatch={dispatch}
            record={study.record}
            uploading={assets.uploading}
            error={study.error ?? assets.sourceError}
            linkRef={linkRef}
            onAddFiles={(files) => void assets.addFiles(files)}
          />,
          {
            continueLabel: "Read my brand",
            continueIcon: <Sparkles size={16} />,
            busy: study.studying,
            validate: () =>
              state.sources.length === 0 ? { reason: "Add a link or a file", focus: linkRef.current } : null,
            onContinue: () => {
              void study.start();
              go("reading");
            },
          },
        );

      case "reading":
        return (
          <ReadingStep
            record={study.record}
            error={study.error}
            settled={settled}
            matched={matched}
            onKeepAll={() => {
              dispatch({ type: "keepAll", value: true });
              go(nextStep(state, "reading", { keepAll: true }));
            }}
            onWalk={() => {
              dispatch({ type: "keepAll", value: false });
              go("logo");
            }}
            onDefaults={() => {
              dispatch({ type: "dropStudyPatch" });
              go("logo");
            }}
            onContinue={() => go("logo")}
            onRetry={() => void study.start()}
            onEditSources={() => {
              study.stop();
              go("sources");
            }}
          />
        );

      case "logo":
        return framed(
          <LogoStep
            state={state}
            fileName={assets.logoFileName}
            error={assets.logoError}
            note={assets.logoNote}
            studyFoundLogo={foundLogo}
            onResetReelLogo={() => {
              for (const c of state.chips)
                if (c.field.startsWith(LOGO_CHIP_PREFIX)) dispatch({ type: "resetChip", field: c.field });
            }}
            onFile={(file) => void assets.handleLogo(file)}
          />,
        );

      case "colors":
        return framed(<ColorsStep state={state} dispatch={dispatch} />);

      case "font":
        return framed(
          <FontStep
            state={state}
            dispatch={dispatch}
            families={libraryFamilies}
            error={assets.fontError ?? libraryError}
            note={assets.fontNote}
            onUploadFont={(file) => void assets.handleFontFile(file)}
          />,
        );

      case "captionSize":
        return framed(<CaptionSizeStep state={state} dispatch={dispatch} />);

      case "captionPosition":
        return framed(<CaptionPositionStep state={state} dispatch={dispatch} />);

      case "speaker":
        return framed(<SpeakerStep state={state} dispatch={dispatch} families={libraryFamilies} />);

      case "signoff":
        return framed(<SignoffStep state={state} dispatch={dispatch} />);

      case "review":
        // Not framed: Review's primary is "Create my brand", not Continue,
        // and its error sentence belongs under THAT button.
        return (
          <div style={{ display: "grid", gap: adjust.adjusting ? 14 : 26, minWidth: 0 }}>
            {!adjust.adjusting && (
              <StepHeader
                chapter={chapterLabel}
                n={counter?.n ?? null}
                total={counter?.total ?? null}
                headline={headline}
                why={copy.why}
              />
            )}
            <ReviewStep
              state={state}
              adjusting={adjust.adjusting}
              onAdjust={adjust.setAdjusting}
              onChange={(id) => go(id, "review")}
              onCreate={() => void createBrand(null)}
              createdSlug={createdSlug}
              onSlug={(v) => dispatch({ type: "patch", patch: { slug: slugify(v), slugTouched: true } })}
              onBack={onBackDefault}
              submitting={submitting}
              error={error}
              studio={
                <OnboardingStudio
                  draftKey={BRAND_SETUP_DRAFT_KEY}
                  inputProps={sample.inputProps}
                  stepPatch={state.designPatch}
                  lift={state.adjustLift}
                  onDesignChange={adjust.onDesignChange}
                  stageHeight={stageHeight}
                />
              }
            />
          </div>
        );

      case "done":
        return (
          <div style={{ display: "grid", gap: 26 }}>
            <StepHeader chapter="Finish" n={null} total={null} headline={headline} why={copy.why} />
            <DoneStep
              state={state}
              slug={createdSlug ?? slug}
              submitting={submitting}
              error={error}
              samplesOn={samplesOn}
              onFinish={leaveWith}
            />
          </div>
        );
    }
  })();

  // ---- render ------------------------------------------------------------

  // Desktop: as tall as the viewport allows (the width follows from 9:16 and
  // is capped by the column). Phone: the spec's 248 × 440, pulled in on a
  // short viewport so the step's control is never pushed under the fold.
  const phoneHeight = wide ? Math.min(Math.round(vh * 0.88), 800) : Math.min(440, Math.round(vh * 0.45));
  const pct = visible.length > 1 ? (indexOfStep(state.step) / (visible.length - 1)) * 100 : 0;
  // A step the walk has passed but that still WANTS something — a refresh
  // drops the uploaded logo and font bytes. The logo clears itself: the
  // wordmark fills the gap the moment the step is reached, so only a step with
  // nothing on screen is marked undone (the sentence stays either way).
  const undone = useMemo(() => {
    const out = new Set<StepId>();
    if (assets.logoNote && !state.logoDataUrl) out.add("logo");
    if (assets.fontNote) out.add("font");
    return out;
  }, [assets.logoNote, assets.fontNote, state.logoDataUrl]);

  const rail = (
    <TimelineRail
      steps={visible}
      current={state.step}
      furthest={furthestVisible}
      counter={counter}
      studied={studied}
      undone={undone}
      notice={draft.quota ? QUOTA_SENTENCE : null}
      onExit={onCancel}
      onStartOver={() => setConfirmStartOver(true)}
      onGo={(id) => {
        // Leaving the reading screen mid-read is a question, not a jump.
        if (state.step === "reading" && study.studying) {
          setConfirmStopReading(true);
          return;
        }
        go(id);
      }}
    />
  );

  /**
   * WHICH moment of the sample each step is about.
   *
   * Cue 0 is the one-line caption (colour, font, position, the sign-off over
   * it), cue 2 is the long sentence the size step is really asking about, and
   * the speaker step wants the CARD, which is on screen at 0.6 s. A step whose
   * phone is parked on a cue that doesn't show what it changes is a step the
   * operator has to answer blind.
   */
  const shot = PHONE_SHOT[state.step] ?? { focusCue: 0 as const };
  const phone = (
    <SamplePhone
      inputProps={sample.inputProps}
      designPatch={effectiveDesign(state)}
      focusCue={shot.focusCue}
      seekSec={shot.seekSec}
      height={phoneHeight}
      expandable={wide && shot.expandable === true}
      overlaysAlwaysOn={shot.overlaysAlwaysOn === true}
    />
  );

  return (
    <div ref={rootRef} style={{ position: "relative", minHeight: `calc(100dvh - ${topInset}px)` }}>
      {sample.fontFaceCss && <style>{sample.fontFaceCss}</style>}
      <ProgressBar pct={pct} />

      <WizardLayout
        wide={wide}
        roomy={roomy}
        adjusting={adjust.adjusting}
        topInset={topInset}
        chapterLabel={chapterLabel}
        counter={counter}
        step={stepView}
        rail={rail}
        phone={phone}
        onExit={onCancel}
        onStartOver={() => setConfirmStartOver(true)}
      />

      {confirmStartOver && (
        <Confirm
          title="Start over? Everything you've picked so far goes."
          confirmLabel="Start over"
          cancelLabel="Keep setting up"
          onConfirm={() => {
            setConfirmStartOver(false);
            onStartOver();
          }}
          onCancel={() => setConfirmStartOver(false)}
        />
      )}

      {confirmStopReading && (
        <Confirm
          title="Stop reading and go back to your sources?"
          confirmLabel="Stop reading"
          cancelLabel="Keep reading"
          onConfirm={() => {
            setConfirmStopReading(false);
            study.stop();
            go("sources");
          }}
          onCancel={() => setConfirmStopReading(false)}
        />
      )}
    </div>
  );
}

/**
 * Is this create failure "that name is taken"?
 *
 * The STATUS is the contract — 409. The message fallback is not belt and
 * braces, it is this server: `POST /api/channels` wraps every `createChannel`
 * throw in one catch-all 400 (`src/server/index.ts`), so the only way to tell
 * a taken slug from a malformed payload today is the sentence it carries. The
 * status test is what will keep working when that route learns to answer 409.
 */
function isNameTaken(status: number, message: string): boolean {
  if (status === 409) return true;
  return status === 400 && /already exists|already taken/i.test(message);
}
